import { ExpressionField } from '../../common';

export type FormulaOperator = '+' | '-' | '*' | '/';
export type DateFunctionName = 'DATEADD' | 'DATEDIFF';

export interface AggregateFunction {
  key: number;
  value: string;
  label: string;
}

export interface AggregateToken {
  type: 'function';
  functionKey: number;
  field?: ExpressionField;
  hasError?: boolean;
}

export interface DateToken {
  type: 'date';
  name: DateFunctionName;
  datePart: string;
  secondKind: 'number' | 'date';
  secondField?: ExpressionField;
  secondNumber?: number;
  useSecondField?: boolean;
  thirdField?: ExpressionField;
  hasError?: boolean;
}

export interface OperatorToken {
  type: 'operator';
  value: FormulaOperator;
  hasError?: boolean;
}

export interface NumberToken {
  type: 'number';
  value: number;
  hasError?: boolean;
}

export interface ParenthesisToken {
  type: 'parenthesis';
  value: '(' | ')';
}

export type FormulaToken = AggregateToken | DateToken | OperatorToken | NumberToken | ParenthesisToken;

export interface FormulaResult {
  expression: string;
  expressionFields: ExpressionField[];
}

export interface FormulaValidation {
  errors: string[];
  invalidIndexes: number[];
  result?: FormulaResult;
}

export const AGGREGATE_FUNCTIONS: AggregateFunction[] = [
  { key: 1, value: 'SUM', label: 'TOTAL' },
  { key: 2, value: 'AVG', label: 'AVG' },
  { key: 3, value: 'MIN', label: 'MIN' },
  { key: 4, value: 'MAX', label: 'MAX' },
  { key: 5, value: 'COUNT', label: 'COUNT' },
];

export const aggregateLabel = (key: number): string => AGGREGATE_FUNCTIONS.find(entry => entry.key === key)?.label;

export const OPERATORS: { value: FormulaOperator; label: string; forDate: boolean }[] = [
  { value: '+', label: '+', forDate: true },
  { value: '-', label: '−', forDate: true },
  { value: '*', label: '×', forDate: false },
  { value: '/', label: '÷', forDate: false },
];

export const DATE_PARTS = ['day', 'week', 'month', 'year'];

export const FORMULA_ERRORS = {
  invalidFunction: 'modules.fieldConfig.formula.invalidFunction',
  invalidNumber: 'modules.fieldConfig.formula.invalidNumber',
  endsWithOperator: 'modules.fieldConfig.formula.endsWithOperator',
  endsWithParenthesis: 'modules.fieldConfig.formula.endsWithParenthesis',
  unbalancedParentheses: 'modules.fieldConfig.formula.unbalancedParentheses',
  needsDate: 'modules.fieldConfig.formula.needsDate',
  dateParameters: 'modules.fieldConfig.formula.dateParameters',
} as const;

// A field id as ExpressionDict gives it: `1970`, `-85` for the current date, or `1970_45` for a field of a related module.
const AGGREGATE_PATTERN = /^(SUM|AVG|MIN|MAX|COUNT)\(\{\{([^}]+)\}\}\)/;
const DATE_PATTERN = /^(DATEADD|DATEDIFF)\(([^)]*)\)/;
const NUMBER_PATTERN = /^\d+(\.\d+)?/;
const FIELD_PARAMETER = /^\{\{([^}]+)\}\}$/;

export const dateFunctionFor = (isDateResult: boolean): DateFunctionName => (isDateResult ? 'DATEADD' : 'DATEDIFF');

export const newDateToken = (isDateResult: boolean): DateToken => ({
  type: 'date',
  name: dateFunctionFor(isDateResult),
  datePart: DATE_PARTS[0],
  secondKind: isDateResult ? 'number' : 'date',
  useSecondField: true,
});

const findField = (key: string, numericFields: ExpressionField[], dateFields: ExpressionField[]) =>
  numericFields.find(field => String(field.key) === key) ?? dateFields.find(field => String(field.key) === key);

export const parseExpression = (
  expression: string,
  numericFields: ExpressionField[],
  dateFields: ExpressionField[],
): FormulaToken[] => {
  const tokens: FormulaToken[] = [];
  let rest = (expression ?? '').trim();

  while (rest.length) {
    const dateMatch = rest.match(DATE_PATTERN);
    const aggregateMatch = rest.match(AGGREGATE_PATTERN);
    const numberMatch = rest.match(NUMBER_PATTERN);
    const first = rest[0];

    if (dateMatch) {
      const [datePart, second, third] = dateMatch[2].split(',');
      const name = dateMatch[1] as DateFunctionName;
      const token: DateToken = {
        type: 'date',
        name,
        datePart: DATE_PARTS.includes(datePart) ? datePart : DATE_PARTS[0],
        secondKind: name === 'DATEADD' ? 'number' : 'date',
      };
      const secondField = second?.match(FIELD_PARAMETER);
      if (secondField) {
        token.secondField = findField(secondField[1], numericFields, dateFields);
        token.useSecondField = true;
      } else if (second !== undefined) {
        token.secondNumber = parseInt(second, 10);
      }
      const thirdField = third?.match(FIELD_PARAMETER);
      if (thirdField) {
        token.thirdField = findField(thirdField[1], numericFields, dateFields);
      }
      tokens.push(token);
      rest = rest.slice(dateMatch[0].length);
    } else if (aggregateMatch) {
      const functionKey = AGGREGATE_FUNCTIONS.find(entry => entry.value === aggregateMatch[1]).key;
      tokens.push({ type: 'function', functionKey, field: findField(aggregateMatch[2], numericFields, dateFields) });
      rest = rest.slice(aggregateMatch[0].length);
    } else if (['+', '-', '*', '/'].includes(first)) {
      tokens.push({ type: 'operator', value: first as FormulaOperator });
      rest = rest.slice(1);
    } else if (first === '(' || first === ')') {
      tokens.push({ type: 'parenthesis', value: first });
      rest = rest.slice(1);
    } else if (numberMatch) {
      tokens.push({ type: 'number', value: parseFloat(numberMatch[0]) });
      rest = rest.slice(numberMatch[0].length);
    } else {
      rest = rest.slice(1);
    }
  }
  return tokens;
};

const fieldParameter = (field: ExpressionField) => `{{${field.key}}}`;

const buildExpression = (tokens: FormulaToken[]): FormulaResult => {
  const expressionFields: ExpressionField[] = [];
  const parts = tokens.map(token => {
    switch (token.type) {
      case 'date': {
        const second = token.secondField ? fieldParameter(token.secondField) : String(token.secondNumber);
        if (token.secondField) {
          expressionFields.push(token.secondField);
        }
        expressionFields.push(token.thirdField);
        return `${token.name}(${token.datePart},${second},${fieldParameter(token.thirdField)})`;
      }
      case 'function': {
        expressionFields.push(token.field);
        const name = AGGREGATE_FUNCTIONS.find(entry => entry.key === token.functionKey).value;
        return `${name}(${fieldParameter(token.field)})`;
      }
      default:
        return String(token.value);
    }
  });
  return { expression: parts.join(''), expressionFields };
};

const dateTokenIncomplete = (token: DateToken) =>
  !(token.useSecondField ? token.secondField : token.secondNumber) || !token.thirdField;

export const validateFormula = (tokens: FormulaToken[], isDateResult: boolean): FormulaValidation => {
  const errors = new Set<string>();
  const invalidIndexes = new Set<number>();

  tokens.forEach((token, index) => {
    if (token.type === 'function' && !token.field) {
      invalidIndexes.add(index);
      errors.add(FORMULA_ERRORS.invalidFunction);
    }
    if (token.type === 'number' && !token.value) {
      invalidIndexes.add(index);
      errors.add(FORMULA_ERRORS.invalidNumber);
    }
  });

  if (!errors.size && tokens.length) {
    const lastIndex = tokens.length - 1;
    const last = tokens[lastIndex];
    if (last.type === 'operator') {
      invalidIndexes.add(lastIndex);
      errors.add(FORMULA_ERRORS.endsWithOperator);
    }
    const opened = tokens.filter(token => token.type === 'parenthesis' && token.value === '(').length;
    const closed = tokens.filter(token => token.type === 'parenthesis' && token.value === ')').length;
    if (last.type === 'parenthesis' && last.value === '(') {
      errors.add(FORMULA_ERRORS.endsWithParenthesis);
    } else if (opened !== closed) {
      errors.add(FORMULA_ERRORS.unbalancedParentheses);
    }
  }

  if (isDateResult && tokens.length && !tokens.some(token => token.type === 'date')) {
    errors.add(FORMULA_ERRORS.needsDate);
  }

  tokens.forEach((token, index) => {
    if (token.type === 'date' && dateTokenIncomplete(token)) {
      invalidIndexes.add(index);
      errors.add(FORMULA_ERRORS.dateParameters);
    }
  });

  return {
    errors: Array.from(errors),
    invalidIndexes: Array.from(invalidIndexes),
    result: errors.size ? undefined : buildExpression(tokens),
  };
};

export const formulaSnapshot = (tokens: FormulaToken[]): string =>
  JSON.stringify(
    tokens.map(token => {
      switch (token.type) {
        case 'function':
          return [token.type, token.functionKey, token.field?.key];
        case 'date':
          return [
            token.type,
            token.name,
            token.datePart,
            token.useSecondField ? token.secondField?.key : token.secondNumber,
            token.thirdField?.key,
          ];
        default:
          return [token.type, token.value];
      }
    }),
  );

const isParenthesis = (token: FormulaToken, value: '(' | ')') => token?.type === 'parenthesis' && token.value === value;

export const canAddOpeningParenthesis = (tokens: FormulaToken[]): boolean => {
  const last = tokens[tokens.length - 1];
  if (!last) {
    return true;
  }
  return last.type === 'operator' || (last.type === 'parenthesis' && last.value === '(');
};

export const canAddClosingParenthesis = (tokens: FormulaToken[]): boolean => {
  const last = tokens[tokens.length - 1];
  const open = tokens.filter(token => isParenthesis(token, '(')).length;
  const closed = tokens.filter(token => isParenthesis(token, ')')).length;
  return open > closed && last.type !== 'operator' && !isParenthesis(last, '(');
};

export const operatorsAround = (tokens: FormulaToken[], index: number) => {
  const neighbourIsDate = tokens[index - 1]?.type === 'date' || tokens[index + 1]?.type === 'date';
  return neighbourIsDate ? OPERATORS.filter(operator => operator.forDate) : OPERATORS;
};

export const isOperatorTurn = (tokens: FormulaToken[]): boolean => {
  const last = tokens[tokens.length - 1];
  if (!last || last.type === 'operator') {
    return false;
  }
  return !(last.type === 'parenthesis' && last.value === '(');
};

const partnerOf = (tokens: FormulaToken[], index: number): number => {
  const own = (tokens[index] as ParenthesisToken).value;
  const step = own === '(' ? 1 : -1;
  let depth = 0;
  for (let position = index; position >= 0 && position < tokens.length; position += step) {
    const token = tokens[position];
    if (token.type === 'parenthesis') {
      depth += token.value === own ? 1 : -1;
      if (!depth) {
        return position;
      }
    }
  }
  return -1;
};

// An operand is a single token or a whole group; a group that never closes runs to the end.
const operandEnd = (tokens: FormulaToken[], index: number): number => {
  if (!isParenthesis(tokens[index], '(')) {
    return index;
  }
  const partner = partnerOf(tokens, index);
  return partner < 0 ? tokens.length - 1 : partner;
};

const withoutRange = (tokens: FormulaToken[], from: number, to: number) =>
  tokens.filter((_, position) => position < from || position > to);

const withoutOperand = (tokens: FormulaToken[], from: number, to: number): FormulaToken[] => {
  if (isParenthesis(tokens[from - 1], '(') && isParenthesis(tokens[to + 1], ')')) {
    return withoutOperand(tokens, from - 1, to + 1);
  }
  if (tokens[to + 1]?.type === 'operator') {
    return withoutRange(tokens, from, to + 1);
  }
  if (tokens[from - 1]?.type === 'operator') {
    return withoutRange(tokens, from - 1, to);
  }
  return withoutRange(tokens, from, to);
};

/**
 * Removes a token together with whatever keeps operands and operators alternating, so the formula
 * never ends up with two operands side by side: an operand leaves with one neighboring operator
 * (a group left empty leaves as a whole), an operator with the operand after it, and a parenthesis
 * with its partner, keeping what was inside.
 */
export const withoutToken = (tokens: FormulaToken[], index: number): FormulaToken[] => {
  const token = tokens[index];
  if (token.type === 'parenthesis') {
    const partner = partnerOf(tokens, index);
    return tokens.filter((_, position) => position !== index && position !== partner);
  }
  if (token.type === 'operator') {
    const next = tokens[index + 1];
    const end = next && !isParenthesis(next, ')') ? operandEnd(tokens, index + 1) : index;
    return withoutRange(tokens, index, end);
  }
  return withoutOperand(tokens, index, index);
};
